// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getUserConsultantProfile } from "@/services/agoraService";
import type { ConsultantProfile } from "@/types/agora";

export interface AgoraAuthContextType {
  isConsultant: boolean;
  consultantProfile: ConsultantProfile | null;
  isLoadingConsultantStatus: boolean;
  refreshConsultantStatus: () => Promise<void>;
}

const AgoraAuthContext = createContext<AgoraAuthContextType | undefined>(undefined);

export function useAgoraAuth() {
  const context = useContext(AgoraAuthContext);
  if (context === undefined) {
    throw new Error('useAgoraAuth must be used within an AgoraAuthProvider');
  }
  return context;
}

export function AgoraAuthProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, profile } = useAuth();
  const [isConsultant, setIsConsultant] = useState<boolean>(false);
  const [consultantProfile, setConsultantProfile] = useState<ConsultantProfile | null>(null);
  const [isLoadingConsultantStatus, setIsLoadingConsultantStatus] = useState<boolean>(true);

  const checkConsultantStatus = async () => {
    if (!isAuthenticated || !profile) {
      setIsConsultant(false);
      setConsultantProfile(null);
      setIsLoadingConsultantStatus(false);
      return;
    }

    setIsLoadingConsultantStatus(true);
    try {
      const consultantData = await getUserConsultantProfile();
      
      if (consultantData && consultantData.status === 'active') {
        setIsConsultant(true);
        setConsultantProfile(consultantData);
      } else {
        setIsConsultant(false);
        setConsultantProfile(null);
      }
    } catch (error) {
      console.error("Error checking consultant status:", error);
      setIsConsultant(false);
      setConsultantProfile(null);
    } finally {
      setIsLoadingConsultantStatus(false);
    }
  };

  const refreshConsultantStatus = async () => {
    await checkConsultantStatus();
  };

  useEffect(() => {
    checkConsultantStatus();
  }, [isAuthenticated, profile]);

  return (
    <AgoraAuthContext.Provider value={{
      isConsultant,
      consultantProfile,
      isLoadingConsultantStatus,
      refreshConsultantStatus
    }}>
      {children}
    </AgoraAuthContext.Provider>
  );
}

export default AgoraAuthContext; 